// import React from "react"
import moment from "moment";
import { useSelector } from "react-redux";
import { getGeocode } from "use-places-autocomplete";
import { ADDRESS_REGEX } from "../constants";



const countryCurrency: Record<string, string> = {
  US: '$',
  IN: '₹',
  //Add possible branch countruies here
};

function getCurrencySymbol(countryCode: string, styled: boolean = false): any {
  return countryCurrency?.[countryCode] ? (
    styled ? (
      <span style={{ fontFamily: 'sans-serif' }}>{countryCurrency[countryCode]}</span>
    ) : (
      countryCurrency[countryCode]
    )
  ) : (
    ''
  );
}

function getFormattedAmount(amount: number | string, countryCode: string = 'US') {
  const amountToFormat = Number(amount || 0);
  return `${getCurrencySymbol(countryCode)}${amountToFormat.toFixed(2)}`;
}

// helper to extract component by type
function getComponent(components = [], type: any) {
  const c: any = components.find((comp: any) => comp.types && comp.types.indexOf(type) !== -1);
  return c ? c.long_name : '';
}
async function getFullAddressFromPlace(placeId: any) {
  return new Promise((resolve, reject) => {
    if (!window.google || !window.google.maps) {
      return reject(new Error('Google Maps JS not loaded'));
    }
    const placesService = new window.google.maps.places.PlacesService(
      document.createElement('div')
    );
    const geocoder = new window.google.maps.Geocoder();
    // 1) Try Places Details first
    placesService.getDetails(
      { placeId, fields: ['address_components', 'geometry', 'formatted_address'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const comps: any = place.address_components || [];
          const zipcode = getComponent(comps, 'postal_code');
          if (zipcode) {
            return resolve({
              source: 'places_details',
              formatted_address: place.formatted_address || '',
              zipcode,
              city: getComponent(comps, 'locality') || getComponent(comps, 'postal_town') || '',
              state: getComponent(comps, 'administrative_area_level_1') || '',
              raw: place,
            });
          }
        }
        // 2) Fallback - geocode by place_id
        geocoder.geocode({ placeId }, (results, gStatus) => {
          if (gStatus === window.google.maps.GeocoderStatus.OK && results && results.length) {
            const comps2: any = results[0].address_components || [];
            const zipcode2 = getComponent(comps2, 'postal_code');
            if (zipcode2) {
              return resolve({
                source: 'geocode_placeid',
                formatted_address: results[0].formatted_address || '',
                zipcode: zipcode2,
                city: getComponent(comps2, 'locality') || getComponent(comps2, 'postal_town') || '',
                state: getComponent(comps2, 'administrative_area_level_1') || '',
                raw: results[0],
              });
            }
            // If geocode by placeId didn't have postal_code, try reverse geocode by lat/lng (if available)
            const latlng =
              place && place.geometry && place.geometry.location
                ? place.geometry.location
                : results[0].geometry && results[0].geometry.location
                ? results[0].geometry.location
                : null;
            if (latlng) {
              geocoder.geocode({ location: latlng }, (revResults, revStatus) => {
                if (
                  revStatus === window.google.maps.GeocoderStatus.OK &&
                  revResults &&
                  revResults.length
                ) {
                  const comps3: any = revResults[0].address_components || [];
                  const zipcode3 = getComponent(comps3, 'postal_code');
                  if (zipcode3) {
                    return resolve({
                      source: 'reverse_geocode',
                      formatted_address: revResults[0].formatted_address || '',
                      zipcode: zipcode3,
                      city:
                        getComponent(comps3, 'locality') ||
                        getComponent(comps3, 'postal_town') ||
                        '',
                      state: getComponent(comps3, 'administrative_area_level_1') || '',
                      raw: revResults[0],
                    });
                  }
                }
                // nothing found
                return resolve({
                  source: 'google_no_postal',
                  zipcode: '',
                  note: 'Google returned no postal_code for this location',
                  raw: { place, results },
                });
              });
              return;
            }
            // no latlng to reverse geocode
            return resolve({
              source: 'no_latlng',
              zipcode: '',
              note: 'No lat/lng to reverse geocode',
              raw: { place, results },
            });
          } else {
            // geocode by place_id failed - we can’t extract postal code from Google
            return resolve({
              source: 'geocode_failed',
              zipcode: '',
              note: `Geocoder status: ${gStatus}`,
            });
          }
        });
      }
    );
  });
}


function buildAddressLine1(suggestion:any, components:any) {
  let parts = suggestion.split(",").map((p:any) => p.trim());

  const removeSet = new Set();

  components.forEach((c:any) => {
    if (
      c.types.includes("locality") ||
      c.types.includes("administrative_area_level_1") ||
      c.types.includes("country") ||
      c.types.includes("postal_code")
    ) {
      removeSet.add(c.long_name.trim());
      removeSet.add(c.short_name.trim());
    }
  });

  parts.pop();
  let filtered = parts.filter((p:any) => !removeSet.has(p));
  
   
  // filtered = filtered.slice(0, filtered.length - 1);
  return filtered.join(", ").trim();
}


const parseAddressFromGoogle = async (addressString: string, LocationPlaceId: string) => {
  try {
    const results = await getGeocode({ address: addressString });
    if (!results || results.length === 0) throw new Error('No results found');
    const result = results[0];
    let streetNumber, route, city, zipcode, state, addressLine1, neighborhood,Sublocality,apartment,fullAddress="";
    const placeId = LocationPlaceId && LocationPlaceId !== '' ? LocationPlaceId : result?.place_id;
    const fulladdress = async () => {
      const res: any = await getFullAddressFromPlace(placeId);  
      const components = res.raw.address_components;     
      const getComponent = (types: string[]) =>
        components.find((c: any) => types.some(type => c.types.includes(type)))?.long_name || '';
      streetNumber = getComponent(['street_number']); 
      neighborhood= getComponent(['neighborhood'])|| getComponent (['sublocality_level_2']);
      Sublocality=  getComponent (['sublocality_level_1']) || getComponent (['sublocality']) ;
      route = getComponent(['route']);
      city = getComponent(['locality', 'administrative_area_level_2']);
      state = getComponent(['administrative_area_level_1']);
      zipcode = getComponent(['postal_code']);
      apartment=getComponent(['subpremise']);
      fullAddress = res.formatted_address;      
      addressLine1 = buildAddressLine1(addressString, components);
      // addressLine1 = [streetNumber, route].filter(Boolean).join(', ');
    // const line1Parts = [
    //     streetNumber,
    //     route,
    //     neighborhood,
    //     Sublocality
    //   ].filter(Boolean);

    //   addressLine1 = line1Parts.join(', ');
      if (!addressLine1) {
        addressLine1 = res.formattedAddress?.split(',')[0] || city;
      }
      return res;
    };
     await fulladdress();

    return {
      addressLine1,
      apartment: '',
      fullAddress,
      state,
      city,
      zipcode,
    };
  } catch (error) {
    console.error('Failed to parse address:', error);
    return null;
  }
};

// const CurrentCurrency = ({restaurantCountry}: {restaurantCountry:string}) => {
//   return (
//     <>
//       {restaurantCountry == "US" ? (
//         <React.Fragment>&#36;&nbsp;</React.Fragment>
//       ) : restaurantCountry == "IN" ? (
//         <React.Fragment> &#x20B9;&nbsp;</React.Fragment>
//       ) : null}
//     </>
//   );
// };

function validateDate(date: Date) {
  const today = new Date();
  return date >= today;
}
function formatDate(date: any, eventSlot?: any, timeZone?: any) {
  // Extract the abbreviated month
  const newDate = moment(date)
  // const timePart = newDate?.format('hh:mm A');
  // const datePart = moment(newDate).format('MMM DD -');
  const timePart = newDate.format('hh:mm A');
  const datePart = newDate.format('MMM DD -');

  if (eventSlot) {
    return `${newDate?.format('MMM DD')} - [${eventSlot}]`;
  }
  return `${datePart + ' ' + timePart}`;
}

function validateInput(
  text: string,
  { regex = '^[a-zA-Z0-9_]*$', charLimit = 15 }: { regex?: string; charLimit?: number }
) {
  if (regex) {
    const re = new RegExp(regex);
    return re.test(text);
  } else {
    return text.length <= charLimit;
  }
}
function convertTo24Hour(timeStr: string) {
  //"11:40: AM"
  const [time, modifier] = timeStr.split(' ');
  let [hours, minutes]: any[] = time.split(':');

  hours = parseInt(hours, 10);

  if (modifier === 'PM' && hours !== 12) {
    hours += 12;
  } else if (modifier === 'AM' && hours === 12) {
    hours = 0;
  }

  return `${String(hours).padStart(2, '0')}:${minutes}:00`;
}

function getCardType(number: string) {
  number = number.replace(/\D/g, '');
  const patterns: { [key: string]: RegExp } = {
    // visa: /^4/,
    // mastercard: /^5[1-5]/,
    // amex: /^3[47]/,
    // discover: /^6(?:011|5)/,
    // More strict regex
    visa: /^4[0-9]{12}(?:[0-9]{3})?$/,
    mastercard: /^5[1-5][0-9]{14}$/,
    amex: /^3[47][0-9]{13}$/,
    discover:
      /^6(?:011\d{12}|5\d{14}|4[4-9]\d{13}|22(?:1(?:2[6-9]|[3-9]\d)|[2-8]\d{2}|9(?:[01]\d|2[0-5]))\d{10})$/,
    // rupay: /^(60|65|81)/,
  };

  for (const card in patterns) {
    if (patterns[card].test(number)) {
      return card;
    }
  }
  return '';
}

function toKey(str: string = '') {
  return str.replace(/\s+/g, '').toLowerCase();
}

async function convertImageToBase64(url: string): Promise<string | null> {
  try {
    const response = await fetch(url, { mode: 'cors' });
    const blob = await response.blob();

    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (e) {
    console.warn('Base64 conversion failed for', url, e);
    return null;
  }
}

function generateNextTenYears() {
  const years = [];
  const currentYear = new Date().getFullYear();

  for (let i = 0; i < 10; i++) {
    years.push((currentYear + i).toString());
  }
  return years;
}

const isToday = (date: Date) => {
  const now = new Date();
  return (
    date.getDate() === now.getDate() &&
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  );
};
const segmentsToRemove = ['/restaurantinfo', '/exploremenu'];
const revampSegments = ['/ReservationRevamp', '/CheckinRevamp'];

const cleanedPath = (path: string, removeLastPath: boolean = false) => {
  let pathUrl = removeLastPath ? path.substring(0, path.lastIndexOf('/')) : path;

  for (const seg of revampSegments) {
    const idx = pathUrl.indexOf(seg);
    if (idx !== -1) {
      pathUrl = pathUrl.substring(0, idx);
    }
  }

  pathUrl = segmentsToRemove.reduce((acc, segment) => {
    return acc.replace(segment, '');
  }, pathUrl);

  if (pathUrl.length > 1 && pathUrl.endsWith('/')) {
    pathUrl = pathUrl.slice(0, -1);
  }

  return pathUrl;
}

export const sanitizeAddress = (value: string) => {
  return value
    // remove invalid characters
    .replace(ADDRESS_REGEX, '')

    // collapse multiple spaces → single space
    .replace(/\s{2,}/g, ' ')

    // collapse repeated special chars (.... ,,, -- //)
    .replace(/([.,'#\-\/()])\1+/g, '$1');
};

export const validateAddress = (value: string) => {
  if (value?.length > 0 && value !== value.trim()) {
    return 'No leading or trailing spaces allowed';
  }

  // consecutive special characters
  if (/([.,'#\-\/()]){2,}/.test(value)) {
    return 'No consecutive special characters allowed';
  }

  // ends with more than one . or ,
  if (/[.,]{2,}$/.test(value)) {
    return 'Address may end with only one . or ,';
  }

  // ends with special char other than . or ,
  if (/[#\-\/()']$/.test(value)) {
    return 'Address cannot end with special characters';
  }

  return true;
};

export   const validateTag = (value: string) => {
  if (value?.length > 0 && value !== value.trim()) {
    return 'Tag name cannot have leading or trailing spaces';
  }

  // consecutive special characters
  if (/([.,'#\-\/()]){2,}/.test(value)) {
    return 'Tag name cannot have consecutive special characters';
  }

  // ends with more than one . or ,
  if (/[.,]{2,}$/.test(value)) {
    return 'Tag name cannot end with more than one . or ,';
  }

  // ends with special char other than . or ,
  if (/[#\-\/()']$/.test(value)) {
    return 'Tag name cannot end with special characters';
  }

  return true;
};

// Helper function to check if a value is empty or only spaces
export const isEmptyOrWhitespace = (value: any): boolean => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'string') {
    return value.trim() === '' || value.trim().toLowerCase() === 'null';
  }
  return false;
};

// Helper function to check if an object has any empty strings, space-filled values, or null values
export const hasEmptyOrInvalidValues = (obj: any): boolean => {
  if (obj === null || obj === undefined) return true;
  
  // Check if it's a string
  if (typeof obj === 'string') {
    return isEmptyOrWhitespace(obj);
  }
  
  // Check if it's an array
  if (Array.isArray(obj)) {
    return obj.some(item => hasEmptyOrInvalidValues(item));
  }
  
  // Check if it's an object (but not Date, RegExp, etc.)
  if (typeof obj === 'object' && obj.constructor === Object) {
    return Object.keys(obj).some(key => {
      const value = obj[key];
      return hasEmptyOrInvalidValues(value);
    });
  }
  
  // For other types (numbers, booleans, etc.), they're considered valid
  return false;
};




const getTwoDecimalValue = (value: any) => {
  return parseFloat(value).toFixed(2);
};

export {
  getFormattedAmount,
  getCurrencySymbol,
  parseAddressFromGoogle,
  // CurrentCurrency,
  formatDate,
  validateDate,
  validateInput,
  convertTo24Hour,
  getCardType,
  toKey,
  convertImageToBase64,
  generateNextTenYears,
  isToday,
  cleanedPath,
  getTwoDecimalValue,
};
